import React, { useState } from 'react';
import { SealCheck, ArrowCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { errText } from '../../../utils/errors';
import { monitoringApi, fmtDateTime } from './monitoringUtils';

// Botón «Monitorizado» / «Quitar monitorizado» del pie del cuestionario.
export const MonitorFooterButton = ({ studyId, target, monitored, isMonitor, completed, missingCount, onChanged, lang }) => {
  const es = lang === 'es';
  const [busy, setBusy] = useState(false);
  const [showUndo, setShowUndo] = useState(false);
  const [reason, setReason] = useState('');

  const reasonOff = !isMonitor ? (es ? 'Solo el monitor del estudio' : 'Study monitor only')
    : !completed ? (es ? 'El cuestionario no está terminado' : 'The questionnaire is not finished')
      : missingCount > 0 ? (es ? (missingCount === 1 ? 'Falta 1 pregunta por marcar' : `Faltan ${missingCount} preguntas por marcar`) : `${missingCount} question(s) left to mark`) : '';

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(okMsg);
      onChanged(data.monitored ?? null);
      return true;
    } catch (e) { toast.error(errText(e)); return false; } finally { setBusy(false); }
  };

  if (monitored) {
    return (
      <>
        <span className="text-[11px] text-teal-700 self-center" data-testid="fq-monitored-info">
          {es ? 'Monitorizado por' : 'Monitored by'} {monitored.by_name || monitored.by} · {fmtDateTime(monitored.at, lang)}
        </span>
        {isMonitor && (
          <Button variant="outline" data-testid="fq-unmonitor-btn" onClick={() => { setReason(''); setShowUndo(true); }}
            className="rounded-sm gap-1 text-teal-700 border-teal-300 hover:bg-teal-50">
            <ArrowCounterClockwise size={14} />{es ? 'Quitar monitorizado' : 'Remove monitored'}
          </Button>
        )}
        <Dialog open={showUndo} onOpenChange={setShowUndo}>
          <DialogContent className="max-w-md" data-testid="fq-unmonitor-dialog">
            <DialogHeader>
              <DialogTitle>{es ? 'Quitar monitorizado' : 'Remove monitored'}</DialogTitle>
              <DialogDescription>{es ? 'Indica el motivo. Quedará registrado en auditoría.' : 'Enter the reason. It will be logged in the audit trail.'}</DialogDescription>
            </DialogHeader>
            <Textarea data-testid="fq-unmonitor-reason" value={reason} onChange={e => setReason(e.target.value)} className="min-h-[80px]" />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowUndo(false)}>{es ? 'Cancelar' : 'Cancel'}</Button>
              <Button data-testid="fq-unmonitor-confirm" disabled={busy || !reason.trim()}
                className="bg-teal-700 hover:bg-teal-800 text-white rounded-sm"
                onClick={async () => { if (await run(() => monitoringApi.uncomplete(studyId, target.volunteer_id, { ...target, reason: reason.trim() }), es ? 'Monitorizado retirado' : 'Monitored removed')) setShowUndo(false); }}>
                {es ? 'Confirmar' : 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <span title={reasonOff} data-testid="fq-monitor-btn-wrap">
      <Button data-testid="fq-monitor-btn" disabled={!!reasonOff || busy}
        onClick={() => run(() => monitoringApi.complete(studyId, target.volunteer_id, target), es ? 'Cuestionario monitorizado' : 'Questionnaire monitored')}
        className="rounded-sm gap-1 bg-teal-700 hover:bg-teal-800 text-white disabled:opacity-50">
        <SealCheck size={15} weight="fill" />{es ? 'Monitorizado' : 'Monitored'}
      </Button>
    </span>
  );
};
